import React from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, Sparkles, Zap, X } from "lucide-react";
import { useEntitlements } from "../context/EntitlementsContext";

const ELEFIN_POINTS = [
  "Full Pro access, free", "Instant trade & balance sync", "Unlimited accounts",
  "Connect in seconds, no queue", "Full analytics & reports", "Extended AI limits", "Priority support",
];
const XM_POINTS = [
  "Full Pro access, free", "Instant trade & balance sync", "Unlimited trade logging",
  "Full analytics & reports", "AI coach & chart analysis",
];

const Onboarding = () => {
  const navigate = useNavigate();
  const { ent, loading } = useEntitlements();

  const dismissed = typeof window !== "undefined" && localStorage.getItem("ws_onboarded") === "1";
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (loading || !ent) return;
    // Show once for demo users who haven't connected a broker and haven't dismissed it.
    if (ent.demo && !dismissed) setOpen(true);
  }, [ent, loading, dismissed]);

  if (!open) return null;

  const go = (broker) => {
    localStorage.setItem("ws_onboarded", "1");
    localStorage.setItem("ws_pref_broker", broker);
    setOpen(false);
    navigate("/connect");
  };
  const close = () => { localStorage.setItem("ws_onboarded", "1"); setOpen(false); };

  return (
    <div className="ws-onboard-overlay" data-testid="onboarding">
      <div className="ws-onboard-modal ws-fade-up">
        <button className="ws-onboard-close" onClick={close} data-testid="onboarding-close" aria-label="Close"><X className="w-5 h-5" /></button>

        <div className="text-center mb-8">
          <span className="ws-onboard-pill"><Zap className="w-3.5 h-3.5" /> Get Started</span>
          <h1 className="ws-heading text-3xl sm:text-4xl font-extrabold mt-4">
            Welcome to <span className="ws-title-gradient">Wealth Secret</span>
          </h1>
          <p className="text-[color:var(--ws-muted)] mt-2 text-sm sm:text-base">Connect your broker to unlock the full platform — free</p>
        </div>

        <div className="ws-onboard-grid">
          {/* Elefin — highlighted (shown first) */}
          <div className="ws-onboard-card ws-onboard-card-featured ws-shine" data-testid="onboarding-elefin" style={{ animationDelay: "80ms" }}>
            <div className="ws-onboard-tags">
              <span className="ws-onboard-tag ws-onboard-tag-rec">RECOMMENDED</span>
              <span className="ws-onboard-tag ws-onboard-tag-exc">EXCLUSIVE</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="ws-onboard-logo ws-onboard-logo-tile"><img src="/brokers/elefin.png" alt="Elefin" /></div>
              <div>
                <div className="ws-heading text-lg font-bold text-white flex items-center gap-2">
                  Elefin <span className="text-[color:var(--ws-cyan)] text-2xl font-extrabold">FREE</span>
                </div>
                <div className="text-[12px] text-[color:var(--ws-cyan)] font-semibold tracking-wide">PRO INCLUDED, ON US</div>
              </div>
            </div>
            <p className="text-[13px] text-white/90 font-medium mb-3">Built heavy. <span className="ws-title-gradient font-semibold">Executes light-speed.</span></p>
            <ul className="ws-onboard-points">
              {ELEFIN_POINTS.map((p) => <li key={p}><span className="ws-onboard-check ws-onboard-check-hot"><Check className="w-3 h-3" /></span> {p}</li>)}
            </ul>
            <button className="ws-onboard-btn ws-onboard-btn-elefin w-full mt-5" onClick={() => go("Elefin")} data-testid="onboarding-connect-elefin">
              <span className="ws-cta-shine" aria-hidden />
              Connect Elefin <ArrowRight className="w-4 h-4" />
            </button>
            <span className="ws-onboard-watermark">elefin</span>
          </div>

          {/* XM — full Pro access (shown second) */}
          <div className="ws-onboard-card ws-onboard-card-xm ws-shine" data-testid="onboarding-xm" style={{ animationDelay: "160ms" }}>
            <div className="ws-onboard-tags">
              <span className="ws-onboard-tag ws-onboard-tag-pro">PRO INCLUDED</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="ws-onboard-logo ws-onboard-logo-tile"><img src="/brokers/xm.png" alt="XM" /></div>
              <div>
                <div className="ws-heading text-lg font-bold text-white flex items-center gap-2">
                  XM <span className="text-[#34d399] text-xl font-extrabold">FREE</span>
                </div>
                <div className="text-[12px] text-[#34d399] font-semibold tracking-wide">FULL PRO · $0/MONTH</div>
              </div>
            </div>
            <p className="text-[13px] text-white/90 font-medium mb-3">Trusted worldwide. <span className="ws-title-gradient font-semibold">Every Pro tool unlocked.</span></p>
            <ul className="ws-onboard-points">
              {XM_POINTS.map((p) => <li key={p}><span className="ws-onboard-check"><Check className="w-3 h-3" /></span> {p}</li>)}
            </ul>
            <button className="ws-onboard-btn ws-onboard-btn-xm w-full mt-5" onClick={() => go("XM")} data-testid="onboarding-connect-xm">
              Connect XM <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <button className="ws-onboard-skip" onClick={close} data-testid="onboarding-skip">
          Explore in demo mode first
        </button>
      </div>
    </div>
  );
};

export default Onboarding;
